import { useToast } from '@airship-platform/airship-patterns';
import {
  DescriptionEditable,
  ManageableProduct,
  ManageProductOptions,
  ManageProductSection,
  ManageProductTechnicianContent,
  useManageProducts,
  useProducts,
} from '@airship-platform/airship-proposal';
import {
  EmptyState,
  FlexBox,
  Loader,
} from '@airship-platform/jam-web/components';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAppointmentProposalOptionProducts } from '../../data-access/appointmentProposalOptionProducts';
import { useAppointmentProposalOptions } from '../../data-access/appointmentProposalOptions';
import { useAppointments } from '../../data-access/appointments';
import { useDurationBilling } from '../../data-offline/features/membershipData/useDurationBilling';

const AddProductPage: React.FC = () => {
  const {
    selectedProducts,
    displaySettings,
    updateProduct,
    setProducts,
    setDisplaySettings,
  } = useManageProducts();
  const { currentAppointment } = useAppointments();
  const { options, currentOption } = useAppointmentProposalOptions();
  const {
    createManyAppointmentProposalOptionProducts,
    getNextSortOrder,
    isLoadingProductIds,
  } = useAppointmentProposalOptionProducts();
  const { showToast } = useToast();
  const navigate = useNavigate();

  const { products, isLoading, isLoaded } = useProducts();
  const { productId } = useParams<{ productId: string }>();
  const durationBillingItems = useDurationBilling(productId ?? '');

  const defaultDurationBilling = durationBillingItems.filter(
    (item) => item.active !== false
  )[0];

  useEffect(() => {
    const filtredProducts = products
      .filter((p) => p.pricebookId === productId)
      .map((product) => {
        const price = product.prices.find(
          (price) => price.priceType === product.defaultPriceType
        ) || {
          value: 0,
          currency: 'USD',
          taxable: true,
        };

        const durationPrice =
          defaultDurationBilling?.useMembershipPricingTable !== false
            ? defaultDurationBilling?.salePrice
            : undefined;
        return {
          ...product,
          quantity: 1,
          price: durationPrice ?? price.value,
          currency: price.currency,
          taxable: price.taxable,
          durationBillingId: defaultDurationBilling?.durationBillingId,
          ...(durationPrice != null && {
            prices: product.prices.map((p) =>
              p.priceType === product.defaultPriceType
                ? { ...p, value: durationPrice }
                : p
            ),
          }),
        };
      });

    setProducts(filtredProducts);

    return () => {
      setProducts([]);
    };
  }, [productId, products, setProducts, defaultDurationBilling]);

  if (isLoading && !isLoaded) {
    return <Loader isLoading message="Loading Product" />;
  }

  if (!currentOption || !selectedProducts.length) {
    return (
      <EmptyState title="No option or product selected" subtitle="">
        <Button component={Link} to={'../'}>
          Go back to products
        </Button>
      </EmptyState>
    );
  }

  const isReadOnly = currentOption.isReadOnly;

  const setSelectedOptionIds = (ids: string[]) =>
    setDisplaySettings({ selectedOptionIds: ids });

  const onCancel = () => {
    setProducts([]);
    navigate('../');
  };

  const handleAddProduct = () => {
    const selectedOpts = options.filter((opt) =>
      displaySettings.selectedOptionIds.includes(
        opt.appointmentProposalOptionId
      )
    );

    const productsNames = selectedProducts.map((u) => u.name).join(', ');

    const payloads = selectedOpts.flatMap((opt) =>
      selectedProducts.map((product, idx) => ({
        appointmentProposalOptionId: opt.appointmentProposalOptionId,
        product: {
          ...product,
          pricebookPrices: product.prices,
          linkedItems: product.linkedItems.filter(
            (item) =>
              item.priceType ===
              (product.defaultPriceType === 'DYNAMIC'
                ? 'STANDARD'
                : product.defaultPriceType)
          ),
          jobId: opt.jobId,
          sortOrder: getNextSortOrder(idx),
        },
      }))
    );

    createManyAppointmentProposalOptionProducts(
      currentOption.appointmentId,
      currentOption.appointmentProposalId,
      payloads
    ).then(() => {
      showToast(`${productsNames} added to selected options`, 'success');
      onCancel();
    });
  };

  const onUpdate =
    (productId: string) => (product: Partial<ManageableProduct>) =>
      updateProduct(productId, product);

  return (
    <FlexBox width={'100%'} flexDirection={'column'} p={[2, 4]} gap={3}>
      <Typography variant="h4">Add item</Typography>

      {selectedProducts.map((product) => (
        <ManageProductSection key={product.pricebookId} product={product}>
          <ManageProductTechnicianContent
            product={product}
            hasMembership={!!currentAppointment?.memberships?.length}
            isLoading={isLoadingProductIds.includes(product.pricebookId)}
            isDisabled={isReadOnly || !displaySettings.selectedOptionIds.length}
            priceIsEditable={!isReadOnly}
            durationBillingItems={durationBillingItems}
            onUpdateProduct={onUpdate(product.pricebookId)}
            onCancel={onCancel}
            onSubmit={handleAddProduct}
          >
            <ManageProductOptions
              options={options}
              selectedOptionIds={displaySettings.selectedOptionIds}
              setSelectedOptionIds={setSelectedOptionIds}
              isLoading={isLoadingProductIds.includes(product.pricebookId)}
            />
            <Divider />
          </ManageProductTechnicianContent>

          {product.description !== undefined && (
            <>
              <Divider />
              <DescriptionEditable
                description={product.description}
                onUpdateDescription={(newDescription) =>
                  onUpdate(product.pricebookId)({
                    description: newDescription,
                  })
                }
                isEditable
              />
            </>
          )}
        </ManageProductSection>
      ))}
    </FlexBox>
  );
};

export default AddProductPage;
